import React, { useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { SwedishCompanyFormOrganizationNumber } from './swedish-company-form-organization-number';
import { SwedishCompanyFormSignup } from './swedish-company-form-signup';
import { SwedishCompanyFormConfirm } from './swedish-company-form-confirm';
import { SwedishCompanyFormCompleted } from './swedish-company-form-completed';
import { formRules } from './form-rules';
import { getCompanyDetails, getMoneyTransferLicenseAgreement } from './api';
import FormHeader from '../../common/form/form-header';
import { KountBlockedMessage } from '../../common/kount-blocked-message';
import { FormFooter } from '../../common/form/form-footer';
import EventEmitter from 'tradera/apps/registration/helpers/event-emitter';
import { triggerUserSignedUpEvent } from 'tradera/apps/registration/helpers/blueshift';
import { Trans, useTranslator } from 'tradera/lang/translate';
import { touchWebClient } from 'tradera/utils/http';
import { BankIdModal } from 'tradera/components/bank-id/bank-id-modal';
import { startAuthAndOpenModal } from 'tradera/components/bank-id/state/bank-id/actions';
import GoogleTagManager from '../../../services/google-tag-manager';
import { waitForCountSessionId } from 'tradera/apps/registration/components/common/kount-session-id-awaiter';
import { SwedishCompanyFormEmail } from 'tradera/apps/registration/components/forms/swedish-company-form-v2/swedish-company-form-email';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import { useQualtive } from 'tradera/hooks/use-qualtive';
import { SURVEYS } from 'tradera/constants/surveys';

const steps = {
    email: 'email',
    start: 'start',
    signup: 'signup',
    confirm: 'confirm'
};

export const SwedishCompanyForm = ({ antiForgeryToken }) => {
    const { t } = useTranslator();
    const [step, setStep] = useState(steps.email);
    const [serverErrors, setServerErrors] = useState({});
    const [autoFillCompanyDetails, setAutoFillCompanyDetails] = useState(false);
    const [successfulSubmissionData, setSuccessfulSubmissionData] =
        useState(null);
    const [
        moneyTransferLicenseAgreementName,
        setMoneyTransferLicenseAgreementName
    ] = useState('');

    const qualtiveRegEmailEnabled = useIsFeatureEnabled('qualtive-reg-email');

    useEffect(() => {
        EventEmitter.dispatch('Form:Loaded', {
            name: 'swedish-company',
            step: 1
        });

        const run = async () => {
            const agreementResponse = await getMoneyTransferLicenseAgreement();
            if (agreementResponse?.data?.agreementName) {
                setMoneyTransferLicenseAgreementName(
                    agreementResponse.data.agreementName
                );
            }
        };

        run();
    }, []);

    const { postQualtiveEvent } = useQualtive();

    useEffect(() => {
        window.location.hash = `#${step}`;
    }, [step]);

    const rules = formRules(t, antiForgeryToken, serverErrors, setServerErrors);
    const form = useForm({
        mode: 'onBlur',
        defaultValues: {
            organizationNumber: '',
            companyName: '',
            streetName: '',
            zipCode: '',
            town: '',
            alias: '',
            emailAddress: '',
            contactFirstName: '',
            contactLastName: '',
            contactSsn: '',
            terms: false,
            emailTerms: false,
            marketing: false,
            moneyTransferLicenseAgreementName: ''
        }
    });

    const handleClickBackLink = useMemo(() => {
        if (step === steps.start) return () => setStep(steps.email);
        if (step === steps.signup) return () => setStep(steps.start);
        if (step === steps.confirm) return () => setStep(steps.signup);

        return undefined;
    }, [step]);

    const getCurrentPage = () => {
        if (step === steps.start) return 2;
        if (step === steps.signup) return 2;
        if (step === steps.confirm) return 3;

        return undefined;
    };

    const handleEmailData = () => {
        const emailAddress = form.getValues('emailAddress');
        if (qualtiveRegEmailEnabled) {
            postQualtiveEvent(
                SURVEYS.swedishCompanyRegEmail.survey,
                emailAddress
            );
        }
        setStep(steps.start);
    };

    const handleCompanyData = async () => {
        const organizationNumber = form.getValues('organizationNumber');
        const result = await getCompanyDetails(
            organizationNumber,
            antiForgeryToken
        );

        setAutoFillCompanyDetails(isCompanyDataComplete(result.data));
        form.setValue('companyName', result.data?.companyName);
        form.setValue('streetName', result.data?.streetName);
        form.setValue('zipCode', result.data?.zipCode);
        form.setValue('town', result.data?.town);

        setStep(steps.signup);
    };

    const isCompanyDataComplete = (data) => {
        return (
            data?.organizationNumber &&
            data.companyName &&
            data.streetName &&
            data.zipCode &&
            data.town
        );
    };

    const handleSignupData = () => {
        setStep(steps.confirm);
    };

    const dispatch = useDispatch();
    const triggerBankIdVerification = (data) => {
        const organizationNumber = form.getValues('organizationNumber');
        const contactSsn = form.getValues('contactSsn');

        if (!organizationNumber || !contactSsn) return;

        dispatch(
            startAuthAndOpenModal(
                () => onSubmit(data),
                () => {
                    console.error('problems with signatory bankid verify');
                },
                'user-reg-company',
                contactSsn,
                organizationNumber
            )
        );
    };

    const onSubmit = async (data) => {
        try {
            const sessionId = await waitForCountSessionId();
            const result = await touchWebClient.post(
                '/api/register/swedish-company',
                {
                    ...data,
                    sessionId
                },
                {
                    headers: {
                        ['X-Xsrf-Token']: antiForgeryToken
                    }
                }
            );

            EventEmitter.dispatch('Form:Submit:Success', result.data);

            GoogleTagManager.pushToDataLayer({
                event: 'user-signup-success',
                'member-id': result.data.memberId
            });
            GoogleTagManager.trackEvent({
                category: 'Login and Signup',
                action: 'Signup confirm - swedish company'
            });
            setSuccessfulSubmissionData({
                emailAddress: result.data.emailAddress,
                redirectLink: result.data.redirectLink
            });
            triggerUserSignedUpEvent(
                result.data.memberId,
                result.data.emailAddress
            );
        } catch (err) {
            if (err.response?.data?.errors) {
                setServerErrors(
                    err.response.data.errors.reduce((serverErrors, error) => {
                        serverErrors[error.field] = error.code;
                        return serverErrors;
                    }, {})
                );
                EventEmitter.dispatch('Form:Submit:Failure', {
                    errors: err.response.data.errors
                });
            }
        }
    };

    return (
        <>
            {!successfulSubmissionData && (
                <>
                    <FormHeader
                        showLanguageMenu={step === steps.email}
                        currentPage={getCurrentPage()}
                        onBack={handleClickBackLink}
                    />
                    <KountBlockedMessage />
                    <FormProvider {...form}>
                        <form
                            onSubmit={form.handleSubmit(
                                triggerBankIdVerification
                            )}>
                            {step === steps.email && (
                                <SwedishCompanyFormEmail
                                    serverErrors={serverErrors}
                                    rules={rules}
                                    onNavigate={handleEmailData}
                                />
                            )}
                            {step === steps.start && (
                                <SwedishCompanyFormOrganizationNumber
                                    serverErrors={serverErrors}
                                    rules={rules}
                                    onNavigate={handleCompanyData}
                                />
                            )}
                            {step === steps.signup && (
                                <SwedishCompanyFormSignup
                                    autoFillCompanyDetails={
                                        autoFillCompanyDetails
                                    }
                                    serverErrors={serverErrors}
                                    rules={rules}
                                    onNavigate={handleSignupData}
                                />
                            )}
                            {step === steps.confirm && (
                                <SwedishCompanyFormConfirm
                                    rules={rules}
                                    serverErrors={serverErrors}
                                    moneyTransferLicenseAgreementName={
                                        moneyTransferLicenseAgreementName
                                    }
                                />
                            )}
                        </form>
                    </FormProvider>
                </>
            )}
            {successfulSubmissionData && (
                <>
                    <div>{/* for flex layout */}</div>
                    <SwedishCompanyFormCompleted
                        redirectLink={successfulSubmissionData.redirectLink}
                        registrationProblemsText={
                            <Trans
                                i18nKey="registration_problems_swedish_company_Trans"
                                // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content
                                components={{ a: <a className="underline" /> }}
                            />
                        }
                    />
                </>
            )}
            <FormFooter registerLink={'/register'} isCompanyLink={false} />

            <BankIdModal />
        </>
    );
};
